// EM²c active industrial rules — on-site construction only.
// Metal Deposit → Mining Outpost → Metal Ore → Ore Refinery → separated metals
// → Factory Complex → components. Regolith Processing supplies foundation /
// hard-structure material. Drones and rovers construct directly at the build site.

import "./economyRules";
import { EM2C_BUILDINGS } from "./em2c-buildings";
import { FACTORY_RECIPES, PRODUCTION_CHAINS } from "./productionChains";

export const BOOTSTRAP_DEFAULT_RECIPES = {
  factory_complex: "machine_parts",
  regolith_processor: "regolith_construction_material",
  waste_recycler: "recycle_salvage",
  gas_refinery: "co_methanation",
  propellant_refinery: "methalox_propellant",
};

// Compatibility: locally assembled Construction Yard modules were removed.
export const LOCAL_MODULE_BUILD_COSTS = Object.freeze({});

export const FIRST_INDUSTRIAL_CHAIN = [
  { building: "mining_outpost", input: null, output: "metal_ore", label: "Mining Outpost" },
  { building: "ore_refinery", input: "metal_ore", output: "iron", label: "Ore Refinery" },
  { building: "factory_complex", input: "iron", output: "machine_parts", label: "Factory Complex" },
  { building: "regolith_processor", input: "regolith", output: "construction_material", label: "Regolith Processing Plant" },
  { building: "ON_SITE_CONSTRUCTION", input: "components + construction material", output: null, label: "Drone / Rover On-Site Construction", finalBuild: true },
];

export function getDefaultRecipeIdForType(type) {
  return BOOTSTRAP_DEFAULT_RECIPES[type] || null;
}

export function getLocalModuleCost() {
  return null;
}

export function canAffordResourceCost(resources, cost) {
  return Object.entries(cost || {}).every(([key, amount]) => Number(resources?.[key]?.stored || 0) + 1e-9 >= Number(amount || 0));
}

export function getPreferredBuildPayment(type, def, resources) {
  if (!def) return null;
  const cost = { ...(def.cost || {}) };
  if (!canAffordResourceCost(resources, cost)) return null;
  return { source: Object.keys(cost).length ? "on_site_materials" : "free", cost, earthCost: cost, moduleCost: null, moduleResource: null };
}

const REMOVED_CHAIN_IDS = new Set([
  "construction_yard",
  "mining_processor",
  "resource_sorter",
  "ice_processor",
  "component_factory",
  "print_factory",
  "habitat_assembly",
  "parts_warehouse",
]);

export function validateFirstIndustrialChain() {
  const errors = [];
  const outpost = EM2C_BUILDINGS.mining_outpost;
  const refinery = EM2C_BUILDINGS.ore_refinery;
  const factory = EM2C_BUILDINGS.factory_complex;
  const regolith = EM2C_BUILDINGS.regolith_processor;

  if (!outpost || outpost.requiresDeposit !== "metal" || !(outpost.productionOutputs?.metal_ore > 0)) {
    errors.push("Mining Outpost must extract Metal Ore from metal deposits");
  }
  if (!refinery || !(refinery.productionInputs?.metal_ore > 0)) {
    errors.push("Ore Refinery must consume Metal Ore");
  }
  if (!refinery || !["iron", "aluminum", "copper", "silicon"].some((key) => refinery.productionOutputs?.[key] > 0)) {
    errors.push("Ore Refinery must output separated usable metals");
  }
  if (!factory || !(FACTORY_RECIPES.factory_complex || []).length) {
    errors.push("Factory Complex must provide component recipes");
  }
  if (!regolith || !(FACTORY_RECIPES.regolith_processor || []).length) {
    errors.push("Regolith Processing Plant must provide building-material processing");
  }

  for (const chain of PRODUCTION_CHAINS || []) {
    for (const stage of chain.stages || []) {
      if (REMOVED_CHAIN_IDS.has(stage.building)) errors.push(`${stage.building} is removed but still appears in ${chain.id}`);
    }
  }

  for (const removed of REMOVED_CHAIN_IDS) {
    if (EM2C_BUILDINGS[removed]) errors.push(`${removed} must not exist in the active building registry`);
  }

  if (errors.length) throw new Error(`[EM²c active industrial chain] Invalid:\n${errors.join("\n")}`);
  return true;
}

export function applyIndustrialBootstrapRules() {
  return validateFirstIndustrialChain();
}

applyIndustrialBootstrapRules();
